import { useState, useEffect, useRef } from 'react';
import { financialReportsApi, formatCurrency, MONTHS } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Upload, FileText, Check, X, 
  Trash2, Edit, Loader2, AlertCircle 
} from 'lucide-react';
import { toast } from 'sonner';

const MAX_REPORTS_PER_MONTH = 3;

export const FinancialReportUpload = ({ propertyId, onDataChange }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(MONTHS[new Date().getMonth()]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [editValues, setEditValues] = useState({});
  const fileInputRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  useEffect(() => {
    fetchReports();
  }, [propertyId]);

  const fetchReports = async () => {
    try {
      const response = await financialReportsApi.getAll(propertyId);
      setReports(response.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group reports by month/year for display
  const groupReportsByMonth = () => {
    const grouped = {};
    reports.forEach(report => {
      const key = `${report.year}-${report.month}`;
      if (!grouped[key]) {
        grouped[key] = {
          month: report.month,
          year: report.year,
          reports: []
        };
      }
      grouped[key].reports.push(report);
    });
    return grouped;
  };

  // Build a full timeline from earliest report to current month, highlighting gaps
  const buildTimeline = () => {
    const grouped = groupReportsByMonth();
    const now = new Date();
    const currentMonthIdx = now.getMonth();
    const currentYear = now.getFullYear();

    // Find the earliest report month/year
    let earliestYear = currentYear;
    let earliestMonthIdx = currentMonthIdx;
    reports.forEach(r => {
      const mi = MONTHS.indexOf(r.month);
      if (r.year < earliestYear || (r.year === earliestYear && mi < earliestMonthIdx)) {
        earliestYear = r.year;
        earliestMonthIdx = mi;
      }
    });

    // If no reports, just return empty
    if (reports.length === 0) return [];

    // Build month-by-month timeline from earliest to current (descending)
    const timeline = [];
    let y = currentYear;
    let m = currentMonthIdx;

    while (y > earliestYear || (y === earliestYear && m >= earliestMonthIdx)) {
      const monthName = MONTHS[m];
      const key = `${y}-${monthName}`;
      const group = grouped[key];

      if (group) {
        timeline.push({ type: 'reports', month: monthName, year: y, reports: group.reports });
      } else {
        // Only flag past months as missing (not the current month)
        const isPast = y < currentYear || (y === currentYear && m < currentMonthIdx);
        if (isPast) {
          timeline.push({ type: 'missing', month: monthName, year: y });
        }
      }

      m--;
      if (m < 0) { m = 11; y--; }
    }

    return timeline;
  };

  // Get count of reports for selected month/year
  const getSelectedMonthReportCount = () => {
    return reports.filter(
      r => r.month === selectedMonth && r.year === selectedYear
    ).length;
  };

  const canUploadMore = () => {
    return getSelectedMonthReportCount() < MAX_REPORTS_PER_MONTH;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }

    setSelectedFile(file);
  };

  const uploadFile = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    if (!canUploadMore()) {
      toast.error(`Maximum ${MAX_REPORTS_PER_MONTH} reports allowed per month`);
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('property_id', propertyId);
      formData.append('month', selectedMonth);
      formData.append('year', selectedYear.toString());

      const response = await financialReportsApi.upload(formData);
      
      if (response.data.status === 'parsed') {
        toast.success('Report uploaded and parsed successfully!');
      } else if (response.data.status === 'failed') {
        toast.error(`Parsing failed: ${response.data.error_message}`);
      }

      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      fetchReports();
      onDataChange?.();
    } catch (error) {
      const message = error.response?.data?.detail || 'Upload failed';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!reportToDelete) return;

    try {
      await financialReportsApi.delete(reportToDelete.id);
      toast.success('Report deleted');
      fetchReports();
      onDataChange?.();
    } catch (error) {
      toast.error('Failed to delete report');
    } finally {
      setDeleteDialogOpen(false);
      setReportToDelete(null);
    }
  };

  const handleEdit = (report) => {
    setEditingReport(report);
    setEditValues({
      rental_income: report.rental_income || '',
      letting_agent_fee: report.letting_agent_fee || '',
      repairs_maintenance: report.repairs_maintenance || '',
    });
    setEditDialogOpen(true);
  };

  const saveEditedValues = async () => {
    if (!editingReport) return;

    try {
      await financialReportsApi.update(editingReport.id, {
        rental_income: editValues.rental_income ? parseFloat(editValues.rental_income) : null,
        letting_agent_fee: editValues.letting_agent_fee ? parseFloat(editValues.letting_agent_fee) : null,
        repairs_maintenance: editValues.repairs_maintenance ? parseFloat(editValues.repairs_maintenance) : null,
      });
      toast.success('Values updated');
      setEditDialogOpen(false);
      fetchReports();
      onDataChange?.();
    } catch (error) {
      toast.error('Failed to update values');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'parsed':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
            <Check className="w-3 h-3 mr-1" /> Parsed
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-100 text-red-700 border-red-200">
            <X className="w-3 h-3 mr-1" /> Failed
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Pending
          </Badge>
        );
      default:
        return null;
    }
  };

  const timeline = buildTimeline();
  const selectedMonthCount = getSelectedMonthReportCount();

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
          <FileText className="w-5 h-5 text-slate-500" />
          Financial Documents
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Upload Section */}
        <div className="bg-slate-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-ink font-display">Upload Landlord Report</h4>
            <span className="text-xs text-slate-500">
              {selectedMonthCount}/{MAX_REPORTS_PER_MONTH} for {selectedMonth} {selectedYear}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Month</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="mt-1" data-testid="report-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(month => (
                    <SelectItem key={month} value={month}>{month}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Year</Label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="mt-1" data-testid="report-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">PDF File</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="flex-1"
                  disabled={!canUploadMore()}
                  data-testid="report-file-input"
                />
                <Button
                  onClick={uploadFile}
                  disabled={!selectedFile || uploading || !canUploadMore()}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="upload-report-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
          {selectedFile && (
            <p className="text-xs text-slate-500 mt-2">
              Selected: {selectedFile.name}
            </p>
          )}
          {!canUploadMore() && (
            <div className="flex items-center gap-2 mt-3 text-amber-600 text-xs">
              <AlertCircle className="w-4 h-4" />
              <span>Maximum {MAX_REPORTS_PER_MONTH} reports reached for {selectedMonth} {selectedYear}. Delete a report to upload a new one.</span>
            </div>
          )}
        </div>

        {/* Uploaded Reports List - Timeline with Missing Month Indicators */}
        <div>
          <h4 className="text-sm font-medium text-ink font-display mb-3">Uploaded Reports</h4>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : timeline.length > 0 ? (
            <div className="space-y-3" data-testid="reports-list">
              {timeline.map(entry => {
                if (entry.type === 'missing') {
                  return (
                    <div
                      key={`missing-${entry.year}-${entry.month}`}
                      className="border border-dashed border-amber-300 bg-amber-50/50 rounded-lg overflow-hidden"
                      data-testid={`missing-month-${entry.year}-${entry.month}`}
                    >
                      <div className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-slate-700">
                            {entry.month} {entry.year}
                          </span>
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            No report uploaded
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                          onClick={() => {
                            setSelectedMonth(entry.month);
                            setSelectedYear(entry.year);
                            // Scroll the upload section into view and open file picker
                            setTimeout(() => {
                              if (fileInputRef.current) {
                                fileInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                setTimeout(() => fileInputRef.current.click(), 400);
                              }
                            }, 50);
                          }}
                          data-testid={`upload-missing-${entry.year}-${entry.month}`}
                        >
                          <Upload className="w-3 h-3 mr-1" />
                          Upload
                        </Button>
                      </div>
                    </div>
                  );
                }

                // Normal report group
                return (
                  <div key={`${entry.year}-${entry.month}`} className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-900">
                        {entry.month} {entry.year}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {entry.reports.length} report{entry.reports.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {entry.reports.map((report, index) => (
                        <div
                          key={report.id}
                          className="flex items-center justify-between p-3 bg-white hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">
                              {index + 1}
                            </div>
                            <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-slate-600 truncate">{report.filename}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {getStatusBadge(report.status)}
                            {report.status === 'parsed' && (
                              <div className="hidden lg:flex items-center gap-4 text-xs text-slate-600 mr-2">
                                <span>Income: {formatCurrency(report.rental_income || 0)}</span>
                                <span>Fees: {formatCurrency(report.letting_agent_fee || 0)}</span>
                                <span>Repairs: {formatCurrency(report.repairs_maintenance || 0)}</span>
                              </div>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(report)}
                              className="h-8 w-8 p-0"
                              data-testid={`edit-report-${report.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setReportToDelete(report);
                                setDeleteDialogOpen(true);
                              }}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                              data-testid={`delete-report-${report.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No reports uploaded yet</p>
              <p className="text-xs mt-1">Upload your letting agent's monthly report (max {MAX_REPORTS_PER_MONTH} per month)</p>
            </div>
          )}
        </div>
      </CardContent>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Report?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete "{reportToDelete?.filename}" for {reportToDelete?.month} {reportToDelete?.year}. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Values Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Parsed Values</DialogTitle>
            <DialogDescription>
              Manually correct the extracted values for {editingReport?.month} {editingReport?.year}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Rental Income (£)</Label>
              <Input
                type="number"
                step="0.01"
                value={editValues.rental_income}
                onChange={(e) => setEditValues({ ...editValues, rental_income: e.target.value })}
                className="mt-1"
                data-testid="edit-rental-income"
              />
            </div>
            <div>
              <Label>Letting Agent Fee (£)</Label>
              <Input
                type="number"
                step="0.01"
                value={editValues.letting_agent_fee}
                onChange={(e) => setEditValues({ ...editValues, letting_agent_fee: e.target.value })}
                className="mt-1"
                data-testid="edit-letting-fee"
              />
            </div>
            <div>
              <Label>Repairs & Maintenance (£)</Label>
              <Input
                type="number"
                step="0.01"
                value={editValues.repairs_maintenance}
                onChange={(e) => setEditValues({ ...editValues, repairs_maintenance: e.target.value })}
                className="mt-1"
                data-testid="edit-repairs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveEditedValues} className="bg-slate-900 hover:bg-slate-800">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default FinancialReportUpload;
